import React, { useState, useRef, useEffect } from 'react';
import { MediaService } from '../../services/mediaService';
import { MediaType } from '../../types';

interface MediaDisplayProps {
  mediaUrls: string[];
  mediaTypes?: MediaType[];
  onMediaClick?: (index: number) => void;
  className?: string;
  autoPlay?: boolean;
  showControls?: boolean;
}

const MediaDisplay: React.FC<MediaDisplayProps> = ({
  mediaUrls,
  mediaTypes,
  onMediaClick,
  className = '',
  autoPlay = false,
  showControls = true
}) => {
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState<{ [key: number]: boolean }>({});
  const videoRefs = useRef<{ [key: number]: HTMLVideoElement | null }>({});

  if (!mediaUrls || mediaUrls.length === 0) return null;

  // Déterminer les types de média si non fournis
  const types = mediaTypes || mediaUrls.map(url => MediaService.getMediaTypeFromUrl(url));

  const handleMediaClick = (index: number) => {
    if (onMediaClick) {
      onMediaClick(index);
    } else {
      setSelectedMediaIndex(index);
    }
  };

  const handleVideoPlay = (index: number) => {
    setIsVideoPlaying(prev => ({ ...prev, [index]: true }));
  };

  const handleVideoPause = (index: number) => {
    setIsVideoPlaying(prev => ({ ...prev, [index]: false }));
  };

  const toggleVideoPlayback = (index: number, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    
    const video = videoRefs.current[index];
    if (video) {
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    }
  };

  const renderMedia = (url: string, type: MediaType, index: number) => {
    switch (type) {
      case 'video':
        return (
          <div 
            key={index} 
            className="relative rounded-xl overflow-hidden bg-black group cursor-pointer"
            onClick={() => handleMediaClick(index)}
          >
            <video
              ref={el => videoRefs.current[index] = el}
              src={url}
              className="w-full h-full object-cover"
              controls={showControls}
              autoPlay={autoPlay}
              muted={autoPlay}
              loop={autoPlay}
              preload="metadata"
              onPlay={() => handleVideoPlay(index)}
              onPause={() => handleVideoPause(index)}
              onLoadedMetadata={(e) => {
                // Optionnel: faire quelque chose quand les métadonnées sont chargées
              }}
            />
            
            {/* Overlay de contrôle personnalisé */}
            {!showControls && (
              <div 
                className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 flex items-center justify-center transition-all duration-200"
                onClick={(e) => toggleVideoPlayback(index, e)}
              >
                {!isVideoPlaying[index] && (
                  <div className="w-16 h-16 bg-emerald-600/80 rounded-full flex items-center justify-center backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                )}
              </div>
            )}
            
            {/* Indicateur vidéo */}
            <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-lg flex items-center space-x-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
              <span>VIDÉO</span>
            </div>
          </div>
        );

      case 'gif':
        return (
          <div 
            key={index} 
            className="relative rounded-xl overflow-hidden group cursor-pointer"
            onClick={() => handleMediaClick(index)}
          >
            <img
              src={url}
              alt={`GIF ${index + 1}`}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            
            {/* Indicateur GIF */}
            <div className="absolute top-2 left-2 bg-emerald-600 text-white text-xs font-bold px-2 py-1 rounded-lg">
              GIF
            </div>
          </div>
        );

      case 'image':
      default:
        return (
          <div 
            key={index} 
            className="relative rounded-xl overflow-hidden group cursor-pointer"
            onClick={() => handleMediaClick(index)}
          >
            <img
              src={url}
              alt={`Image ${index + 1}`}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>
        );
    }
  };

  const renderMediaGrid = () => {
    const mediaCount = mediaUrls.length;

    // Cas spécial pour 1 vidéo - affichage grand format
    if (mediaCount === 1 && types[0] === 'video') {
      return (
        <div className={`mt-3 ${className}`}>
          <div className="aspect-video max-h-96">
            {renderMedia(mediaUrls[0], types[0], 0)}
          </div>
        </div>
      );
    }

    // Cas spécial pour 1 GIF
    if (mediaCount === 1 && types[0] === 'gif') {
      return (
        <div className={`mt-3 ${className}`}>
          <div className="rounded-xl overflow-hidden bg-gradient-to-br from-gray-900/60 to-emerald-900/40 shadow-xl flex items-center justify-center max-h-96">
            {renderMedia(mediaUrls[0], types[0], 0)}
          </div>
        </div>
      );
    }

    // 1 média (image)
    if (mediaCount === 1) {
      return (
        <div className={`mt-3 ${className}`}>
          <div className="rounded-xl overflow-hidden bg-gradient-to-br from-gray-900/60 to-emerald-900/40 shadow-xl flex items-center justify-center max-h-96">
            {renderMedia(mediaUrls[0], types[0], 0)}
          </div>
        </div>
      );
    }

    // 2 médias
    if (mediaCount === 2) {
      return (
        <div className={`mt-3 grid grid-cols-2 gap-3 ${className}`}>
          {mediaUrls.map((url, index) => (
            <div key={index} className="aspect-square rounded-xl overflow-hidden bg-gradient-to-br from-gray-900/60 to-emerald-900/40 shadow-xl">
              {renderMedia(url, types[index], index)}
            </div>
          ))}
        </div>
      );
    }

    // 3 médias
    if (mediaCount === 3) {
      return (
        <div className={`mt-3 grid grid-cols-2 gap-3 ${className}`}>
          <div className="aspect-square rounded-xl overflow-hidden bg-gradient-to-br from-gray-900/60 to-emerald-900/40 shadow-xl">
            {renderMedia(mediaUrls[0], types[0], 0)}
          </div>
          <div className="grid grid-rows-2 gap-3">
            {mediaUrls.slice(1).map((url, index) => (
              <div key={index + 1} className="aspect-square rounded-xl overflow-hidden bg-gradient-to-br from-gray-900/60 to-emerald-900/40 shadow-xl">
                {renderMedia(url, types[index + 1], index + 1)}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // 4+ médias
    return (
      <div className={`mt-3 grid grid-cols-2 gap-3 ${className}`}>
        {mediaUrls.slice(0, 3).map((url, index) => (
          <div key={index} className="aspect-square rounded-xl overflow-hidden bg-gradient-to-br from-gray-900/60 to-emerald-900/40 shadow-xl">
            {renderMedia(url, types[index], index)}
          </div>
        ))}
        <div 
          className="relative aspect-square rounded-xl overflow-hidden bg-gradient-to-br from-gray-900/60 to-emerald-900/40 shadow-xl cursor-pointer"
          onClick={() => handleMediaClick(3)}
        >
          {renderMedia(mediaUrls[3], types[3], 3)}
          {mediaCount > 4 && (
            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-xl flex items-center justify-center">
              <span className="text-white text-xl font-bold">+{mediaCount - 3}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {renderMediaGrid()}
      
      {/* Modal plein écran */}
      {selectedMediaIndex !== null && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedMediaIndex(null)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            {types[selectedMediaIndex] === 'video' ? (
              <video
                src={mediaUrls[selectedMediaIndex]}
                controls
                autoPlay
                className="max-w-full max-h-full object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img
                src={mediaUrls[selectedMediaIndex]}
                alt={`Media ${selectedMediaIndex + 1}`}
                className="max-w-full max-h-full object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            
            <button
              onClick={() => setSelectedMediaIndex(null)}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70"
            >
              ×
            </button>
            
            {/* Navigation */}
            {mediaUrls.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedMediaIndex(prev => 
                      prev === null ? 0 : prev > 0 ? prev - 1 : mediaUrls.length - 1
                    );
                  }}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-70"
                >
                  ‹
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedMediaIndex(prev => 
                      prev === null ? 0 : prev < mediaUrls.length - 1 ? prev + 1 : 0
                    );
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-70"
                >
                  ›
                </button>
              </>
            )}
            
            {/* Compteur */}
            {mediaUrls.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm">
                {selectedMediaIndex + 1} / {mediaUrls.length}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MediaDisplay;
